import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setupWebTelemetry } from '@/shared/observability/otel';
import { setupBrowserAgent } from '@/shared/observability/newRelic';
import { setupCoralogixRum } from '@/shared/observability/coralogixRum';
import { registerPreloadErrorReload } from '@/shared/app/preloadErrorReload';
import { env } from '@/env';
import { App } from './App';
import './theme/tailwind.css';

/**
 * Entry point. Order matters:
 *  1. OTel first — so fetch/XHR instrumentation catches the session bootstrap
 *  2. MSW (dev only) before React mounts, so the first `/auth/me` request is intercepted
 *  3. React mount
 *
 * MSW is dynamically imported behind `import.meta.env.DEV`, so it is
 * tree-shaken out of production bundles.
 */
declare global {
  interface Window {
    __QFLY_DISABLE_MOCKS__?: boolean;
  }
}

const bootstrap = async (): Promise<void> => {
  setupWebTelemetry();
  setupBrowserAgent();
  setupCoralogixRum();
  // Auto-recover stale code-split chunks after a deploy (see module docs) so an
  // already-open tab reloads into the new build instead of showing the router
  // "Unexpected Application Error" page.
  registerPreloadErrorReload();

  const mocksDisabledByTest =
    typeof window !== 'undefined' && window.__QFLY_DISABLE_MOCKS__ === true;

  if (env.enableMocks && import.meta.env.DEV && !mocksDisabledByTest) {
    const { worker } = await import('@/shared/mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element #root not found');
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
};

void bootstrap();
